import type { FC, ReactNode } from 'react'
import type { CartItem, WishlistItem } from '@/types'
import type { SearchProduct } from '@/pages/api/products'

import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react'

const CART_KEY = 'ema-charms:cart'
const WISHLIST_KEY = 'ema-charms:wishlist'
const MAX_PER_LINE = 20

export type AddToCartInput = Omit<CartItem, 'key' | 'quantity'> & {
    quantity?: number
}

export interface ShopToast {
    id: number
    title: string
    product?: { id: string, name: string }
    detail?: string
    action?: 'cart' | 'wishlist'
}

interface ShopContextValue {
    hydrated: boolean
    cart: Array<CartItem>
    cartCount: number
    cartTotal: number
    addToCart: (input: AddToCartInput) => void
    updateQuantity: (key: string, quantity: number) => void
    removeFromCart: (key: string) => void
    clearCart: () => void
    syncCart: (products: Array<SearchProduct>) => void
    wishlist: Array<WishlistItem>
    isWishlisted: (productId: string) => boolean
    toggleWishlist: (item: WishlistItem) => void
    removeFromWishlist: (productId: string) => void
    isCartOpen: boolean
    openCart: () => void
    closeCart: () => void
    isSearchOpen: boolean
    openSearch: () => void
    closeSearch: () => void
    toast: ShopToast | null
    notify: (toast: Omit<ShopToast, 'id'>) => void
    dismissToast: () => void
}

const ShopContext = createContext<ShopContextValue | null>(null)

const readList = (key: string): Array<unknown> => {
    try {
        const raw = window.localStorage.getItem(key)
        const parsed: unknown = raw ? JSON.parse(raw) : []

        return Array.isArray(parsed) ? parsed : []
    }
    catch {
        return []
    }
}

const writeList = (key: string, value: Array<unknown>) => {
    try {
        window.localStorage.setItem(key, JSON.stringify(value))
    }
    catch {
        // Storage can be full or blocked (private mode). The cart still works for this visit.
    }
}

const isCartItem = (value: unknown): value is CartItem => {
    const item = value as CartItem

    return Boolean(item && typeof item.key === 'string' && typeof item.productId === 'string' && typeof item.name === 'string' && Number.isFinite(item.price))
}

const isWishlistItem = (value: unknown): value is WishlistItem => {
    const item = value as WishlistItem

    return Boolean(item && typeof item.productId === 'string' && typeof item.slug === 'string')
}

const clampQuantity = (quantity: number, max: number | null): number => {
    const limit = max && max > 0 ? Math.min(max, MAX_PER_LINE) : MAX_PER_LINE

    return Math.max(1, Math.min(limit, Math.round(quantity)))
}

export const cartItemKey = (productId: string, colorLabel: string | null): string => {
    return `${productId}:${colorLabel ?? 'default'}`
}

export const ShopProvider: FC<{ children: ReactNode }> = ({ children }): ReactNode => {
    const [hydrated, setHydrated] = useState(false)
    const [cart, setCart] = useState<Array<CartItem>>([])
    const [wishlist, setWishlist] = useState<Array<WishlistItem>>([])
    const [isCartOpen, setCartOpen] = useState(false)
    const [isSearchOpen, setSearchOpen] = useState(false)
    const [toast, setToast] = useState<ShopToast | null>(null)
    const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
    const cartRef = useRef<Array<CartItem>>([])

    cartRef.current = cart

    useEffect(() => {
        setCart(readList(CART_KEY).filter(isCartItem))
        setWishlist(readList(WISHLIST_KEY).filter(isWishlistItem))
        setHydrated(true)

        const onStorage = (event: StorageEvent) => {
            if (event.key === CART_KEY) setCart(readList(CART_KEY).filter(isCartItem))
            if (event.key === WISHLIST_KEY) setWishlist(readList(WISHLIST_KEY).filter(isWishlistItem))
        }

        window.addEventListener('storage', onStorage)

        return () => window.removeEventListener('storage', onStorage)
    }, [])

    useEffect(() => {
        if (hydrated) writeList(CART_KEY, cart)
    }, [cart, hydrated])

    useEffect(() => {
        if (hydrated) writeList(WISHLIST_KEY, wishlist)
    }, [wishlist, hydrated])

    useEffect(() => {
        return () => {
            if (toastTimer.current) clearTimeout(toastTimer.current)
        }
    }, [])

    const notify = useCallback((next: Omit<ShopToast, 'id'>) => {
        if (toastTimer.current) clearTimeout(toastTimer.current)

        setToast({ ...next, id: Date.now() })
        toastTimer.current = setTimeout(() => setToast(null), 3200)
    }, [])

    const dismissToast = useCallback(() => {
        if (toastTimer.current) clearTimeout(toastTimer.current)

        setToast(null)
    }, [])

    const addToCart = useCallback((input: AddToCartInput) => {
        const key = cartItemKey(input.productId, input.colorLabel)
        const amount = Math.max(1, Math.round(input.quantity ?? 1))

        setCart(current => {
            const existing = current.find(item => item.key === key)

            if (existing) {
                return current.map(item => item.key === key
                    ? { ...item, ...input, key, quantity: clampQuantity(item.quantity + amount, input.maxQuantity) }
                    : item)
            }

            return [...current, { ...input, key, quantity: clampQuantity(amount, input.maxQuantity) }]
        })

        notify({
            title: 'Ditambahkan ke keranjang',
            product: { id: input.productId, name: input.name },
            detail: input.colorLabel ?? undefined,
            action: 'cart'
        })
    }, [notify])

    // Brings a saved cart in line with the live catalogue: current prices, stock limits, and
    // products that were sold out or taken off the shop.
    const syncCart = useCallback((products: Array<SearchProduct>) => {
        const byId = new Map(products.map(product => [product.id, product]))

        const merge = (items: Array<CartItem>) => {
            let removed = 0
            let trimmed = 0

            const next = items.flatMap(item => {
                const product = byId.get(item.productId)

                if (!product || (!product.madeToOrder && product.stock <= 0)) {
                    removed += 1

                    return []
                }

                const maxQuantity = product.madeToOrder ? null : product.stock
                const quantity = clampQuantity(item.quantity, maxQuantity)

                if (quantity < item.quantity) trimmed += 1

                return [{ ...item, name: product.name, slug: product.slug, price: product.price, image: product.image ?? item.image, maxQuantity, quantity }]
            })

            const changed = next.length !== items.length || next.some((item, index) => {
                const before = items[index]

                return item.price !== before.price || item.quantity !== before.quantity || item.maxQuantity !== before.maxQuantity || item.name !== before.name || item.image !== before.image
            })

            return { next, changed, removed, trimmed }
        }

        // Counts for the notice come from the latest cart; the update itself merges whatever the cart
        // holds at that moment, so a quantity changed while the stock was loading is not lost.
        const { changed, removed, trimmed } = merge(cartRef.current)

        if (!changed) return

        setCart(current => {
            const result = merge(current)

            return result.changed ? result.next : current
        })

        if (removed || trimmed) {
            notify({
                title: removed ? 'Keranjang diperbarui' : 'Jumlah disesuaikan dengan stok',
                detail: removed
                    ? `${removed} produk sudah tidak tersedia dan dihapus dari keranjang`
                    : 'Jumlah pesanan tidak boleh melebihi stok yang tersedia'
            })
        }
    }, [notify])

    const updateQuantity = useCallback((key: string, quantity: number) => {
        setCart(current => current.map(item => item.key === key
            ? { ...item, quantity: clampQuantity(quantity, item.maxQuantity) }
            : item))
    }, [])

    const removeFromCart = useCallback((key: string) => {
        setCart(current => current.filter(item => item.key !== key))
    }, [])

    const clearCart = useCallback(() => setCart([]), [])

    const isWishlisted = useCallback((productId: string) => {
        return wishlist.some(item => item.productId === productId)
    }, [wishlist])

    const toggleWishlist = useCallback((item: WishlistItem) => {
        const exists = wishlist.some(entry => entry.productId === item.productId)

        setWishlist(current => exists
            ? current.filter(entry => entry.productId !== item.productId)
            : [item, ...current.filter(entry => entry.productId !== item.productId)])

        notify({
            title: exists ? 'Dihapus dari favorit' : 'Disimpan ke favorit',
            product: { id: item.productId, name: item.name },
            action: exists ? undefined : 'wishlist'
        })
    }, [wishlist, notify])

    const removeFromWishlist = useCallback((productId: string) => {
        setWishlist(current => current.filter(entry => entry.productId !== productId))
    }, [])

    const value = useMemo<ShopContextValue>(() => ({
        hydrated,
        cart,
        cartCount: cart.reduce((sum, item) => sum + item.quantity, 0),
        cartTotal: cart.reduce((sum, item) => sum + item.quantity * item.price, 0),
        addToCart,
        updateQuantity,
        removeFromCart,
        clearCart,
        syncCart,
        wishlist,
        isWishlisted,
        toggleWishlist,
        removeFromWishlist,
        isCartOpen,
        openCart: () => {
            setSearchOpen(false)
            setCartOpen(true)
        },
        closeCart: () => setCartOpen(false),
        isSearchOpen,
        openSearch: () => {
            setCartOpen(false)
            setSearchOpen(true)
        },
        closeSearch: () => setSearchOpen(false),
        toast,
        notify,
        dismissToast
    }), [
        hydrated,
        cart,
        addToCart,
        updateQuantity,
        removeFromCart,
        clearCart,
        syncCart,
        wishlist,
        isWishlisted,
        toggleWishlist,
        removeFromWishlist,
        isCartOpen,
        isSearchOpen,
        toast,
        notify,
        dismissToast
    ])

    return (
        <ShopContext.Provider value={value}>
            {children}
        </ShopContext.Provider>
    )
}

export const useShop = (): ShopContextValue => {
    const context = useContext(ShopContext)

    if (!context) throw new Error('useShop must be used inside ShopProvider')

    return context
}
